import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { USE_AUTH } from '../auth/auth-interceptor';
import { READ_ONLY_ERROR_CODE, isMutatingApiRequest } from './read-only.constants';
import { ReadOnlyModeService } from './read-only.service';

/**
 * Blocks state-changing API calls for read-only sessions before they leave the
 * browser, and detects an environment-wide freeze reported by the backend.
 *
 * This is defence in depth, not the real control: every write is also rejected
 * by the backend (userClaimParser -> enforceReadOnly). Its value is catching
 * anything a hidden button missed, with a clear error instead of a silent 403.
 */
@Injectable({
  providedIn: 'root',
})
export class ReadOnlyInterceptor implements HttpInterceptor {
  private readonly readOnlyMode = inject(ReadOnlyModeService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.context.get(USE_AUTH) || !this.isApiRequest(req.url)) {
      return next.handle(req);
    }

    const path = this.toApiPath(req.url);

    if (this.readOnlyMode.isReadOnly && isMutatingApiRequest(req.method, path)) {
      console.warn(
        `Read-only mode: blocked ${req.method} ${path} (reason: ${this.readOnlyMode.reason})`,
      );
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 403,
            statusText: 'Forbidden',
            url: req.url,
            error: {
              code: READ_ONLY_ERROR_CODE,
              reason: this.readOnlyMode.reason,
              message: 'You have read-only access. Changes cannot be saved.',
            },
          }),
      );
    }

    // Pick up an environment-wide freeze the frontend cannot see directly.
    return next.handle(req).pipe(
      tap({
        error: (error: unknown) => this.detectGlobalReadOnlyMode(error),
      }),
    );
  }

  private isApiRequest(url: string): boolean {
    return url.startsWith(environment.apiUrl) || url.startsWith(environment.outboundUrl);
  }

  /** Strip the base url and query string so the path matches the route patterns. */
  private toApiPath(url: string): string {
    let path = url;
    for (const base of [environment.apiUrl, environment.outboundUrl]) {
      if (base && path.startsWith(base)) {
        path = path.slice(base.length);
        break;
      }
    }
    return path.split('?')[0] || '/';
  }

  private detectGlobalReadOnlyMode(error: unknown): void {
    if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
      return;
    }

    // httpHandler serialises HttpException as { message, errors: { code, reason } };
    // the client-side block above uses the flat shape.
    const body = error.error as
      | { code?: string; reason?: string; errors?: { code?: string; reason?: string } }
      | undefined;
    const details = body?.errors ?? body;

    if (details?.code === READ_ONLY_ERROR_CODE && details.reason === 'GLOBAL_READ_ONLY_MODE') {
      this.readOnlyMode.markGlobalReadOnlyMode();
    }
  }
}
